import {
  computed, ref, watch,
} from 'vue';
import type { Store } from 'vuex';

export interface StickerHostTarget {
  entity?: string;
  entityId?: string;
  organization?: { id?: string };
  user?: { id?: string };
}

function normalizeOrgId(id: unknown): string | null {
  if (id == null || id === '') return null;
  return String(id);
}

export function useStickerHostCheck(
  store: Store<any>,
  loadMyOrganizations: () => Promise<Array<{ id?: string; orgId?: string }>>,
) {
  const hostCheckReady = ref(false);
  const me = computed(() => store.getters['users/me']);

  const myOrganizationIds = computed(
    () => store.getters['organizations/GET_USER_MANAGED_ORGANIZATION_IDS'] ?? [],
  );

  async function ensureMyOrganizationsLoaded(): Promise<void> {
    if (!me.value?.id) {
      hostCheckReady.value = true;
      return;
    }

    if (myOrganizationIds.value.length > 0) {
      hostCheckReady.value = true;
      return;
    }

    try {
      await loadMyOrganizations();
    } catch (err) {
      console.error('Failed to load user organizations:', err);
    } finally {
      hostCheckReady.value = true;
    }
  }

  function isHostOfSticker(target: StickerHostTarget): boolean {
    if (!me.value) return false;

    const myOrgIds = new Set(
      myOrganizationIds.value
        .map((id: unknown) => normalizeOrgId(id))
        .filter(Boolean) as string[],
    );

    if (target.entity === 'Organization') {
      const stickerOrgIds = [
        normalizeOrgId(target.entityId),
        normalizeOrgId(target.organization?.id),
      ].filter(Boolean) as string[];

      return stickerOrgIds.some((id) => myOrgIds.has(id));
    }

    return normalizeOrgId(me.value.id) === normalizeOrgId(target.user?.id);
  }

  watch(
    () => me.value?.id ?? null,
    async (userId, previousUserId) => {
      if (userId) {
        await ensureMyOrganizationsLoaded();
        return;
      }

      if (previousUserId) {
        store.commit('organizations/MUTATE_USER_MANAGED_ORGANIZATIONS', []);
        hostCheckReady.value = true;
      }
    },
  );

  return {
    hostCheckReady,
    myOrganizationIds,
    ensureMyOrganizationsLoaded,
    isHostOfSticker,
  };
}
